/**
 * HMA Signals
 *
 * Hull Moving Average with slope-based buy/sell coloring.
 * Plots HMA line colored green when rising, red when falling.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA, getSource } from "./sma";

function computeWMA(values: number[], period: number): (number | undefined)[] {
  const result: (number | undefined)[] = new Array(values.length).fill(undefined);
  const denom = (period * (period + 1)) / 2;
  for (let i = period - 1; i < values.length; i++) {
    let sum = 0;
    for (let j = 0; j < period; j++) {
      sum += values[i - period + 1 + j] * (j + 1);
    }
    result[i] = sum / denom;
  }
  return result;
}

export const hmaSignalsIndicator: IndicatorDefinition = {
  id: "hma_signals",
  name: "HMA Signals",
  shortName: "HMASig",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 5, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "HL2", value: "hl2" },
      { label: "HLC3", value: "hlc3" }, { label: "OHLC4", value: "ohlc4" },
    ] },
  ],
  plots: [
    { key: "hma", label: "HMA", color: "#26A69A", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source } = params;
    const src = getSource(candles, source);

    const halfPeriod = Math.floor(period / 2);
    const sqrtPeriod = Math.floor(Math.sqrt(period));

    const wmaHalf = computeWMA(src, halfPeriod);
    const wmaFull = computeWMA(src, period);

    // 2 * WMA(half) - WMA(full)
    const diff: number[] = [];
    for (let i = 0; i < src.length; i++) {
      if (wmaHalf[i] !== undefined && wmaFull[i] !== undefined) {
        diff.push(2 * wmaHalf[i]! - wmaFull[i]!);
      }
    }

    const hma = computeWMA(diff, sqrtPeriod);

    const hmaData: { time: number; value: number }[] = [];
    const offset = src.length - diff.length;

    for (let i = 0; i < diff.length; i++) {
      if (hma[i] !== undefined) {
        const cIdx = i + offset;
        if (cIdx < candles.length) {
          hmaData.push({ time: candles[cIdx].time, value: hma[i]! });
        }
      }
    }

    return [{ key: "hma", data: hmaData }];
  },
};
